/* @flow */
import Base from "nexia_mobile/platforms/base";
import Environment from "nexia_mobile/environment";
import Bridge, { invokeCallback } from "nexia_mobile/platforms/android/bridge";
import type {
  Geofence,
  GeofenceCrossing,
  NativeHTTPRequestOptions,
} from "nexia_mobile/platforms/base";
import { setImplementation } from "nx-components/utils/enqueueIn";

export default class AndroidPlatform extends Base {
  initScriptInterface(callback: () => void) {
    setImplementation({
      enqueue: Bridge.enqueueIn,
      cancel: Bridge.cancelEnqueued,
    });

    Bridge.notifyScriptInterfaceReady();
    Bridge.setPlatformAttributes();
    callback();
  }

  exitApp() {
    Bridge.exitApp();
  }

  allowScreenDimming(yesno: boolean = true) {
    if (yesno) {
      Bridge.allowScreenDimming();
    } else {
      Bridge.keepScreenOn();
    }
  }

  getStoredValue(key: string) {
    return Bridge.getStoredValue(key);
  }

  setStoredValue(key: string, value: string) {
    Bridge.setStoredValue(key, value);
  }

  clearStoredValue(key: string) {
    Bridge.clearStoredValue(key);
  }

  clearStoredValues() {
    Bridge.clearStoredValues();
  }

  canSendFeedback() {
    return true;
  }

  obtainPushNotificationToken() {
    Bridge.obtainPushNotificationToken();
  }

  resetAppBadge() {
    Bridge.clearNotifications();
  }

  hideSoftKeyboard() {
    Bridge.hideSoftKeyboard();
  }

  isImageSaveSupported() {
    return true;
  }

  saveImage(base64Data: string) {
    return Bridge.saveImage(base64Data);
  }

  isMobilePlatform() {
    return true;
  }

  hasAppInstalled(name: string): Promise<boolean> {
    return Promise.resolve(Bridge.hasAppInstalled(name));
  }

  sendGoogleAnalyticsEvent(category: string, action: string, label: string) {
    Bridge.sendGoogleAnalyticsEvent(category, action, label);
  }

  sendGoogleAnalyticsDuration(
    category: string,
    action: string,
    label: string,
    value: ?number
  ) {
    Bridge.sendGoogleAnalyticsTiming(category, action, label, value || 0);
  }

  // Geofencing methods
  isGeofencingSupported() {
    return !Environment.isCommercial();
  }

  isNotificationSoundConfigurable() {
    // keep this synchronous to improve the experience on the local settings page
    return Bridge.isNotificationSoundConfigurable();
  }

  _inflightPermissionPromise: ?Promise<boolean>;

  checkLocationPermissions() {
    if (this._inflightPermissionPromise) {
      // We can only have a single permission request at once
      return this._inflightPermissionPromise;
    }
    return (this._inflightPermissionPromise = Bridge.checkLocationPermissions().then(
      (result) => {
        this._inflightPermissionPromise = null;
        return result;
      }
    ));
  }

  getLocationServicesWarningMessage() {
    return Bridge.getLocationServicesWarningMessage().then((response) => {
      if (response.message) {
        return response.message;
      } else {
        throw new Error(
          "Expected getLocationServicesWarningMessage to return a message"
        );
      }
    });
  }

  areLocationServicesEnabled() {
    return Bridge.areLocationServicesEnabled();
  }

  openAppPermissionSettings() {
    // Android doesn't allow us to open the permission page, just app settings
    Bridge.openAppSettings();
  }

  openLocationServiceSettings() {
    Bridge.openLocationServiceSettings();
  }

  openWifiSettings() {
    Bridge.openWifiSettings();
  }

  setGeofences(geofences: Array<Geofence>): Promise<void> {
    const filteredGeofences = geofences.map(
      ({
        id,
        name,
        latitude,
        longitude,
        radius,
        hrefAcknowledge,
        hrefEnter,
        hrefExit,
      }) => ({
        id,
        name,
        latitude,
        longitude,
        radius: parseInt(radius),
        hrefAcknowledge,
        hrefEnter,
        hrefExit,
      })
    );
    Bridge.setGeofences(filteredGeofences);
    return Promise.resolve();
  }

  startGeofenceService(): Promise<void> {
    Bridge.startGeofenceService();
    return Promise.resolve();
  }

  stopGeofenceService(): Promise<void> {
    Bridge.stopGeofenceService();
    return Promise.resolve();
  }

  getGeofenceCrossings(): Promise<Array<GeofenceCrossing>> {
    return Promise.resolve(Bridge.getGeofenceCrossings());
  }
  // end Geofencing methods

  isVoiceAssistantSupported() {
    return true;
  }

  // Wifi Methods
  // Does the platform allow programmatic access to Wifi networks?
  canGetWifiNetworks() {
    return true;
  }

  // Does the platform allow you to programmatically connect to a Wifi network?
  canConnectToWifiNetwork() {
    return true;
  }

  // Start a Wifi network scan. The SSID of each found network will be passed
  // to callback as an array of strings.
  wifiScan(callback: (Array<string>) => void) {
    Bridge.wifiScan().then(callback);
  }

  // Connect to a Wifi network.
  connectToWifiNetwork(ssid: string, password: string): Promise<void> {
    Bridge.connectToWifiNetwork(ssid, password);
    return Promise.resolve();
  }

  isFingerprintAuthAvailable(): Promise<boolean> {
    return Promise.resolve(Bridge.isFingerprintAuthAvailable());
  }

  authenticateWithFingerprint() {
    return Bridge.authenticateWithFingerprint();
  }

  cancelFingerprintAuthentication() {
    Bridge.cancelFingerprintAuthentication();
  }

  canEnableRemoteDebugging() {
    return true;
  }

  enableRemoteDebugging() {
    Bridge.enableRemoteDebugging();
  }

  canUseNativeHTTP() {
    return true;
  }

  nativeHTTPRequest(options: NativeHTTPRequestOptions) {
    return Bridge.nativeHTTPRequest(options).then((response) => {
      if (response.error) {
        throw new Error(response.errorDescription);
      } else {
        const { body, code } = response;
        return { body, code };
      }
    });
  }

  canDo724OpenAuthEncryption(): boolean {
    return true;
  }

  encryptStringFor724OpenAuth(key: string, input: string): Promise<string> {
    return Promise.resolve(Bridge.encryptStringFor724OpenAuth(key, input));
  }

  showWebView() {
    Bridge.showWebView();
  }

  hideWebView() {
    Bridge.hideWebView();
  }

  // Used by the Java code
  _invokeCallback(callbackName: string, value?: any) {
    invokeCallback(callbackName, value);
  }
}



// WEBPACK FOOTER //
// ./src/platforms/android/index.js